import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Select,
    DatePicker,
    Button,
    Upload,
    Avatar,
    Checkbox
} from 'antd';
import {
    ChevronDown,
    ChevronRight,
    Search,
    Calendar,
    X,
    Plus,
    UploadCloud,
    RefreshCw
} from 'lucide-react';
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import Layout from '../../Layout'; // Giữ nguyên layout của bạn
import { Link } from "react-router-dom";

const { TextArea } = Input;
const { Dragger } = Upload;

export default function AddContractForm() {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // 1. Quản lý trạng thái Đóng/Mở (Collapse)
    const [expandedSections, setExpandedSections] = useState({
        general: true,
        products: true,
        payment: true,
        description: true,
        attachment: true,
        custom: true,
        related: true
    });

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 2. Data và State cho Hàng hóa
    const [products, setProducts] = useState([
        { id: 1, product: 'Techpro-01 - Roaming Video Ngắn', unit: 'Video', price: '1,000,000', suggestedQty: '1', qty: '1', total: '1,000,000' },
        { id: 2, product: 'Nhập sản phẩm, dịch vụ', unit: 'Đơn vị', price: 'Nhập giá mua', suggestedQty: 'SL', qty: 'SL', total: 'Thành tiền' },
        { id: 3, product: 'Nhập sản phẩm, dịch vụ', unit: 'Đơn vị', price: 'Nhập giá mua', suggestedQty: 'SL', qty: 'SL', total: 'Thành tiền' },
    ]);

    const handleAddProduct = () => {
        setProducts([...products, { id: Date.now(), product: 'Nhập sản phẩm, dịch vụ', unit: 'Đơn vị', price: 'Nhập giá mua', suggestedQty: 'SL', qty: 'SL', total: 'Thành tiền' }]);
    };
    const handleRemoveProduct = (id) => {
        setProducts(products.filter(p => p.id !== id));
    };

    // 3. Data và State cho Thanh toán
    const [payments, setPayments] = useState([
        { id: 1, time: 'Lần 1', date: null, amount: '', note: '' },
        { id: 2, time: 'Lần 2', date: null, amount: '', note: '' },
        { id: 3, time: 'Lần 3', date: null, amount: '', note: '' }
    ]);

    const handleAddPayment = () => {
        setPayments([...payments, { id: Date.now(), time: `Lần ${payments.length + 1}`, date: null, amount: '', note: '' }]);
    };
    const handleRemovePayment = (id) => {
        setPayments(payments.filter(p => p.id !== id));
    };

    // Theme Config (Màu chủ đạo #3e91ab)
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
            controlHeight: 40,
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const SectionHeader = ({ title, sectionKey }) => (
        <div
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-8 first:mt-0 cursor-pointer hover:opacity-80 transition-opacity select-none"
        >
            {expandedSections[sectionKey] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span>{title}</span>
        </div>
    );

    // --- Data và State cho Đối tượng liên quan ---
    const relatedObjectOptions = [
        { value: 'co_hoi', label: 'Cơ hội' },
        { value: 'khach_hang', label: 'Khách hàng' },
        { value: 'doi_tac', label: 'Đối tác' },
        { value: 'bao_gia', label: 'Báo giá' },
        { value: 'don_hang_ban', label: 'Đơn hàng bán' },
        { value: 'hop_dong_ban', label: 'Hợp đồng bán' },
        { value: 'hop_dong_mua', label: 'Hợp đồng mua' },
        { value: 'don_hang_mua', label: 'Đơn hàng mua' },
        { value: 'phieu_chi', label: 'Phiếu chi' },
        { value: 'phieu_thu', label: 'Phiếu thu' },
        { value: 'phieu_chuyen_tien', label: 'Phiếu chuyển tiền' },
        { value: 'phieu_nhap_kho', label: 'Phiếu nhập kho' },
        { value: 'phieu_xuat_kho', label: 'Phiếu xuất kho' },
        { value: 'phieu_chuyen_kho', label: 'Phiếu chuyển kho' },
        { value: 'de_xuat_tuyen_dung', label: 'Đề xuất tuyển dụng' },
    ];

    const [relatedObjects, setRelatedObjects] = useState([
        { id: 1, type: null },
        { id: 2, type: null }
    ]);

    const handleAddRelated = () => {
        setRelatedObjects([...relatedObjects, { id: Date.now(), type: null }]);
    };

    const handleRemoveRelated = (id) => {
        setRelatedObjects(relatedObjects.filter(item => item.id !== id));
    };

    return (
        <Layout>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* Header cố định */}
                    <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 md:px-6 py-4 sticky top-[64px] z-10 shadow-sm">
                        <div className="flex items-center gap-2 text-sm mb-2">
                            <Link to="/purchase-order" className="flex items-center gap-2">
                                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white">
                                    <LeftOutlined className="text-[10px]" />
                                </div>
                            </Link>
                            <Link to="/purchase-order" className="text-gray-600 dark:text-gray-400 hover:text-[#3e91ab]">
                                Đơn hàng mua
                            </Link>
                            <span><RightOutlined className="w-3 h-3 text-gray-400" /></span>
                            <span className="text-gray-800 dark:text-gray-200 font-medium">Thêm mới đơn hàng mua</span>
                        </div>
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                            Thêm mới ĐHM
                        </h2>
                    </div>

                    {/* Nội dung form */}
                    <div className="w-full mx-auto rounded-lg shadow-sm p-4 md:p-8 mt-6 dark:bg-gray-800 bg-white border dark:border-gray-700">

                        {/* --- 1. THÔNG TIN CHUNG --- */}
                        <SectionHeader title="Thông tin chung" sectionKey="general" />
                        {expandedSections.general && (
                            <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 mb-6">
                                {/* Hàng 1 */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Ngày đặt hàng <span className="text-red-500">*</span></label>
                                        <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="23/08/2026" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Ngày nhận hàng <span className="text-red-500">*</span></label>
                                        <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="23/08/2026" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Mã đơn hàng mua <span className="text-red-500">*</span></label>
                                        <Input defaultValue="ĐHM_2026_0299" suffix={<RefreshCw size={16} className="text-gray-400 cursor-pointer hover:text-[#3e91ab]" />} />
                                    </div>
                                </div>

                                {/* Các hàng Select dọc */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Kiểu đơn hàng <span className="text-red-500">*</span></label>
                                    <Select defaultValue="1" className="w-full" options={[{ value: '1', label: 'Chiết khấu trên tổng giá trị đơn hàng - Hóa đơn thuế trên tổng đơn hàng' }]} />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Tạo đơn hàng từ</label>
                                    <Select defaultValue="1" className="w-full" options={[{ value: '1', label: 'Một hợp đồng bán' }]} />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Hợp đồng <span className="text-red-500">*</span></label>
                                    <Select defaultValue="1" className="w-full" options={[{ value: '1', label: '9 - Hợp đồng Fmdoc' }]} />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Nhà cung cấp</label>
                                    <Select defaultValue="1" className="w-full" options={[{ value: '1', label: 'buso.com' }]} />
                                </div>

                                {/* SDT, Email, Địa chỉ */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Số điện thoại</label>
                                        <Input defaultValue="0986478827" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Email</label>
                                        <Input defaultValue="nguyenvanduoc@gmail.com" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Địa chỉ</label>
                                    <Input placeholder="Địa chỉ" />
                                </div>

                                {/* Tài khoản, Ngân hàng */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input placeholder="Số tài khoản" />
                                    <Select placeholder="Ngân hàng" className="w-full" options={[]} />
                                </div>

                                {/* Phụ trách, Theo dõi */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Người phụ trách</label>
                                    <Select mode="multiple" defaultValue={['1']} className="w-full" options={[{ value: '1', label: 'HQ_495 - Mr. Huấn' }]} />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Người theo dõi</label>
                                    <Select mode="multiple" defaultValue={['1']} className="w-full" options={[{ value: '1', label: 'BCT127 - Nguyễn Xuân Hòa' }]} />
                                </div>

                                <div className="mt-2">
                                    <Checkbox className="text-gray-600 dark:text-gray-300">Tự động tạo phiếu nhập kho <span className="text-gray-400">❓</span></Checkbox>
                                </div>
                            </div>
                        )}

                        {/* --- 2. NỘI DUNG ĐƠN HÀNG --- */}
                        <div className="flex justify-between items-center mt-8 mb-4">
                            <div
                                onClick={() => toggleSection('products')}
                                className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm cursor-pointer hover:opacity-80 transition-opacity select-none"
                            >
                                {expandedSections.products ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                <span>Nội dung đơn hàng</span>
                            </div>
                            {/* Nút VND */}
                            {expandedSections.products && (
                                <div className="flex items-center text-xs border dark:border-gray-600 rounded">
                                    <span className="px-3 py-1.5 border-r dark:border-gray-600 text-gray-600 dark:text-gray-300">VND</span>
                                    <span className="px-2 py-1.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"><X size={14} className="text-gray-400" /></span>
                                </div>
                            )}
                        </div>

                        {expandedSections.products && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                <div className="overflow-x-auto pb-4">
                                    <div className="min-w-[900px]">
                                        {/* Header Row */}
                                        <div className="grid grid-cols-[40px_3fr_1fr_1.5fr_1fr_1fr_1.5fr_auto] gap-2 mb-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 px-1 items-end">
                                            <span className="text-center">STT</span>
                                            <span>Sản phẩm, dịch vụ <span className="text-red-500">*</span></span>
                                            <span>Đơn vị</span>
                                            <span>Giá mua</span>
                                            <span>SL đề xuất</span>
                                            <span>SL mua <span className="text-red-500">*</span></span>
                                            <span>Thành tiền <span className="text-red-500">*</span></span>
                                            <span className="w-6"></span>
                                        </div>

                                        {/* Product Rows */}
                                        {products.map((item, index) => (
                                            <div key={item.id} className="grid grid-cols-[40px_3fr_1fr_1.5fr_1fr_1fr_1.5fr_auto] gap-2 mb-3 items-center">
                                                <div className="text-center bg-gray-50 dark:bg-gray-700/50 border dark:border-gray-600 rounded h-10 flex items-center justify-center text-gray-500">{index + 1}</div>

                                                <Input
                                                    value={item.product}
                                                    suffix={index === 0 ? <X size={14} className="text-gray-300" /> : <Search size={14} className="text-gray-300" />}
                                                    className={index !== 0 ? "text-gray-400" : ""}
                                                />
                                                <Input value={item.unit} className={index !== 0 ? "text-gray-400" : ""} />
                                                <Input value={item.price} className={`text-right ${index !== 0 ? "text-gray-400" : ""}`} />
                                                <Input value={item.suggestedQty} className={`text-right ${index !== 0 ? "text-gray-400" : ""}`} />
                                                <Input value={item.qty} className={`text-right ${index !== 0 ? "text-gray-400" : ""}`} />
                                                <Input value={item.total} className={`text-right bg-gray-50 dark:bg-gray-700/50 ${index !== 0 ? "text-gray-400" : ""}`} readOnly={index !== 0} />
                                                <X size={18} className="text-gray-400 cursor-pointer hover:text-red-500 ml-1" onClick={() => handleRemoveProduct(item.id)} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <button onClick={handleAddProduct} className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mb-6">
                                    <Plus size={14} />
                                </button>

                                {/* Tổng cộng (Nằm bên phải) */}
                                <div className="flex justify-end mt-4">
                                    <div className="w-full md:w-[450px] flex flex-col gap-3 text-sm">
                                        <div className="flex justify-between items-center gap-4">
                                            <span className="text-gray-600 dark:text-gray-400 flex-1 text-right">Tổng tiền</span>
                                            <Input defaultValue="1,000,000" className="w-[240px] text-right bg-gray-50 dark:bg-gray-700/50" readOnly />
                                        </div>
                                        <div className="flex justify-between items-center gap-4">
                                            <span className="text-gray-600 dark:text-gray-400 flex-1 text-right">Chiết khấu</span>
                                            <div className="flex gap-2 w-[240px]">
                                                <Input placeholder="%" className="w-[80px] text-center" />
                                                <Input placeholder="Hoặc số tiền" className="flex-1 text-right" />
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center gap-4">
                                            <span className="text-gray-600 dark:text-gray-400 flex-1 text-right">Thuế</span>
                                            <div className="flex gap-2 w-[240px]">
                                                <Input placeholder="%" className="w-[80px] text-center" />
                                                <Input placeholder="Hoặc số tiền" className="flex-1 text-right" />
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center gap-4">
                                            <span className="text-gray-600 dark:text-gray-400 flex-1 text-right">Phí</span>
                                            <div className="flex items-center gap-2 w-[240px]">
                                                {/* Đã tăng độ rộng của ô Select này lên w-[80px] để không bị lẹm chữ */}
                                                <Select defaultValue="Phi" className="w-[80px]" options={[{ value: 'Phi', label: 'Phí' }]} />
                                                <Input placeholder="Số tiền" className="flex-1 text-right" />
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center gap-4 mt-2">
                                            <span className="text-gray-500 dark:text-gray-400 flex-1 text-right font-medium">Tổng tiền sau thuế</span>
                                            <Input defaultValue="1,000,000" className="w-[240px] text-right bg-gray-50 dark:bg-gray-700/50 font-medium" readOnly />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- 3. THANH TOÁN --- */}
                        <SectionHeader title="Thanh toán" sectionKey="payment" />
                        {expandedSections.payment && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                <div className="overflow-x-auto pb-4">
                                    <div className="min-w-[700px]">
                                        <div className="grid grid-cols-[80px_2fr_2fr_3fr_auto] gap-2 mb-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 px-1 items-end">
                                            <span>Lần TT</span>
                                            <span>Ngày thanh toán</span>
                                            <span>Số tiền</span>
                                            <span>Nội dung thanh toán</span>
                                            <span className="w-6"></span>
                                        </div>

                                        {payments.map((item) => (
                                            <div key={item.id} className="grid grid-cols-[80px_2fr_2fr_3fr_auto] gap-2 mb-3 items-center">
                                                <Input value={item.time} readOnly className="bg-gray-50 dark:bg-gray-700/50 text-gray-500" />
                                                <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="dd/mm/yyyy" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                                <Input placeholder="Tiền" className="text-right" />
                                                <Input placeholder="Nội dung thanh toán" />
                                                <X size={18} className="text-gray-400 cursor-pointer hover:text-red-500 ml-1" onClick={() => handleRemovePayment(item.id)} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button onClick={handleAddPayment} className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors">
                                    <Plus size={14} />
                                </button>
                            </div>
                        )}

                        {/* --- 4. MÔ TẢ --- */}
                        <SectionHeader title="Mô tả" sectionKey="description" />
                        {expandedSections.description && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                <TextArea placeholder="Ghi chú đơn hàng mua" autoSize={{ minRows: 3, maxRows: 6 }} className="p-3" />
                            </div>
                        )}

                        {/* --- 5. ĐÍNH KÈM --- */}
                        <SectionHeader title="Đính kèm" sectionKey="attachment" />
                        {expandedSections.attachment && (
                            /* Đổi md:w-1/2 thành lg:w-1/2 để tablet hiển thị full chiều rộng */
                            <div className="animate-in slide-in-from-top-2 mb-6 w-full lg:w-1/2">
                                <Dragger className={`bg-transparent`} style={{ padding: '20px', border: `1px dashed ${isDarkMode ? '#4b5563' : '#d1d5db'}` }}>
                                    {/* Thêm flex-col cho mobile và sm:flex-row cho màn hình to hơn */}
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#3e91ab]/10 flex items-center justify-center shrink-0">
                                            <UploadCloud size={20} className="text-[#3e91ab]" />
                                        </div>
                                        <div className="text-center sm:text-left">
                                            <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                                                Kéo thả file vào đây để tải lên hoặc
                                            </p>
                                            {/* Thêm flex-wrap để chống tràn nút */}
                                            <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                                                <Button type="primary" size="small" style={{ backgroundColor: '#3e91ab' }}>
                                                    CHỌN TỪ MÁY
                                                </Button>
                                                <Button size="small" icon={<UploadCloud size={14} />}>
                                                    CHỌN TỪ CLOUD
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Dragger>
                            </div>
                        )}

                        {/* --- 6. TRƯỜNG TÙY BIẾN --- */}
                        <SectionHeader title="Trường tùy biến" sectionKey="custom" />
                        {expandedSections.custom && (
                            <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 mb-6 max-w-md">
                                <Input placeholder="Thu từ khách hàng" suffix={<Search size={16} className="text-gray-400" />} />
                                <Input placeholder="Liên Kết Khách Hàng" suffix={<Search size={16} className="text-gray-400" />} />
                                <Input placeholder="test" />
                            </div>
                        )}

                        {/* --- 7. ĐỐI TƯỢNG LIÊN QUAN --- */}
                        <SectionHeader title="Đối tượng liên quan" sectionKey="related" />
                        {expandedSections.related && (
                            <div className="animate-in slide-in-from-top-2 mb-6 max-w-2xl">
                                {relatedObjects.map((item) => (
                                    <div key={item.id} className="flex items-center gap-3 mb-3">
                                        <Select
                                            placeholder="Đối tượng liên quan"
                                            className="w-full"
                                            options={relatedObjectOptions}
                                            value={item.type}
                                            onChange={(val) => {
                                                setRelatedObjects(relatedObjects.map(obj => obj.id === item.id ? { ...obj, type: val } : obj))
                                            }}
                                        />
                                        <X
                                            size={20}
                                            className="text-gray-400 cursor-pointer hover:text-red-500 shrink-0"
                                            onClick={() => handleRemoveRelated(item.id)}
                                        />
                                    </div>
                                ))}

                                <button
                                    onClick={handleAddRelated}
                                    className="flex items-center justify-center w-8 h-8 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mt-2"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                        )}

                        {/* --- FOOTER BUTTONS --- */}
                        <div className="flex gap-3 pt-6 border-t dark:border-gray-700">
                            <Button type="primary" size="large" style={{ backgroundColor: '#3e91ab', fontWeight: 500 }}>
                                CẬP NHẬT
                            </Button>
                            <Button size="large" className="font-medium">
                                HỦY BỎ
                            </Button>
                        </div>
                    </div>
                </div>
            </ConfigProvider>
        </Layout>
    );
}